/**
 * Safe localStorage utility functions with error handling
 */
import { warn } from './logger';

/**
 * Safely get item from localStorage. Tries to JSON.parse; falls back to the
 * raw string, then to `defaultValue` on miss/parse error.
 */
export const getStorageItem = <T = unknown>(key: string, defaultValue: T | null = null): T | null => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      return defaultValue;
    }
    // Try to parse as JSON, fallback to raw string
    try {
      return JSON.parse(item) as T;
    } catch {
      return item as unknown as T;
    }
  } catch (error) {
    warn(`Failed to get localStorage item "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Safely set item in localStorage (objects are JSON stringified).
 * @returns whether the write succeeded.
 */
export const setStorageItem = (key: string, value: unknown): boolean => {
  try {
    const serialized = typeof value === 'string' ? value : JSON.stringify(value);
    localStorage.setItem(key, serialized);
    return true;
  } catch (error) {
    warn(`Failed to set localStorage item "${key}":`, error);
    return false;
  }
};

/**
 * Safely remove item from localStorage.
 * @returns whether the removal succeeded.
 */
export const removeStorageItem = (key: string): boolean => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    warn(`Failed to remove localStorage item "${key}":`, error);
    return false;
  }
};

/**
 * Clear all localStorage items.
 * @returns whether the clear succeeded.
 */
export const clearStorage = (): boolean => {
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    warn('Failed to clear localStorage:', error);
    return false;
  }
};
